import { isMobile } from '@genially/design-system';
import { FEATURE_FLAGS } from '@genially/ui/build/constants/featureFlags';

import { useModal } from '../../../../../../../bootstrap/application/components/app/components/ModalProvider/ModalProvider.context';
import { SessionConfigModal } from '../../../../../../../shared/application/components/SessionConfigModal/SessionConfigModal';
import { useHostClientContext } from '../../HostClientProvider/hooks/useHostClientContext';
import { useHostGameContext } from '../HostGame.context';

export const useOpenSessionConfigModal = (options?: {
  isInitialConfig?: boolean;
  onClose?: () => void;
}) => {
  const { isInitialConfig = false, onClose } = options ?? {};

  const { openModal, closeModal } = useModal();
  const { client } = useHostClientContext();
  const {
    reactionsConfig,
    raiseHandConfig,
    hostCursorConfig,
    resultsModeConfig,
    aliasModeConfig,
  } = useHostGameContext();

  const showHostCursorToggle = !isMobile;

  const showAnalyticsResultsMode = client.hasFeatureFlag(
    FEATURE_FLAGS.FEAT_LT4_PLAY_ANALYTICS_RESULTS_VIEW,
  );

  const showNoResultsMode = client.hasFeatureFlag(
    FEATURE_FLAGS.FEAT_LT4_PLAY_DISABLE_RANKING,
  );

  const showAliasMode = client.hasFeatureFlag(FEATURE_FLAGS.FEAT_LT4_PLAY_ALIAS_MODE);

  const handleClose = () => {
    closeModal();
    onClose?.();
  };

  const openSessionConfigModal = () => {
    openModal(
      parentElement => (
        <SessionConfigModal
          parentElement={parentElement}
          closeModal={handleClose}
          isInitialConfig={isInitialConfig}
          // ModalProvider snapshots this element when the modal opens, so a plain value
          // here would stay frozen on whatever it was at that moment. The getters keep
          // the reads inside the modal's own (observer) render, where MobX tracks them.
          reactionsConfig={{
            get isEnabled() {
              return client.game.isReactionsEnabled;
            },
            toggle: reactionsConfig.toggle,
          }}
          raiseHandConfig={{
            get isEnabled() {
              return client.game.isRaiseHandEnabled;
            },
            toggle: raiseHandConfig.toggle,
          }}
          hostCursorConfig={hostCursorConfig}
          resultsModeConfig={{
            get mode() {
              return client.game.resultsMode;
            },
            setMode: resultsModeConfig.setMode,
          }}
          aliasModeConfig={{
            get mode() {
              return client.game.aliasMode;
            },
            setMode: aliasModeConfig.setMode,
          }}
          showReactionsToggle
          showRaiseHandToggle
          showHostCursorToggle={showHostCursorToggle}
          showAnalyticsResultsMode={showAnalyticsResultsMode}
          showNoResultsMode={showNoResultsMode}
          showAliasMode={showAliasMode}
        />
      ),
      { blocksBackground: isInitialConfig },
    );
  };

  return { openSessionConfigModal };
};
